import cryptoRandomString from 'crypto-random-string';
import {
    ProcedureArgs,
    ProcedureName,
    ULRpcRequestMessage,
    ULRpcResponseMessage,
    ULRpcResponseErrorMessage,
} from '@ul/common';

// Template for RPC Callers
export abstract class BaseRpcCaller<A extends ProcedureArgs> {
    constructor(rpcId: string) {
        this.rpcId = rpcId;
    }

    private rpcId: string;

    protected abstract sendRequest: <P extends ProcedureName>(request: ULRpcRequestMessage<P, A>) => boolean;

    protected abstract receiveResponse: <P extends ProcedureName>(
        request: ULRpcRequestMessage<P, A>,
        resolve: (value: ULRpcResponseErrorMessage | ULRpcResponseMessage<P>) => void
    ) => () => void;

    // Fire and forget
    protected readonly call = <P extends ProcedureName>(procedureName: P, args: A[P]): void => {
        const request = this.buildRequestMessage(procedureName, args);
        this.sendRequest(request);
    };

    // Wait for response within specified timeout
    protected readonly callAsync = async <P extends ProcedureName>(procedureName: P, args: A[P], timeout: number) => {
        const request = this.buildRequestMessage(procedureName, args);

        let timeoutCleanup = () => {};
        const timeoutPromise = new Promise<never>((_, reject) => {
            const id = setTimeout(
                () => reject(new Error(`UL-RPC.${procedureName} timeout, after waiting for ${timeout}ms`)),
                timeout
            );
            timeoutCleanup = () => clearTimeout(id);
        });

        let responseCleanup = () => {};
        const responsePromise = new Promise<ULRpcResponseErrorMessage | ULRpcResponseMessage<P>>(resolve => {
            responseCleanup = this.receiveResponse(request, resolve);
        });

        try {
            const sent = this.sendRequest(request);
            if (!sent) {
                throw new Error(`UL-RPC.${procedureName} cannot send request`);
            }
            const response = await Promise.race([responsePromise, timeoutPromise]);
            if (response.type === 'errorResponse') {
                throw response.error;
            }
            return response.data;
        } finally {
            responseCleanup();
            timeoutCleanup();
        }
    };

    private readonly buildRequestMessage = <P extends ProcedureName>(
        procedureName: P,
        args: A[P]
    ): ULRpcRequestMessage<P, A> => {
        const nonce = cryptoRandomString({ length: 10, type: 'distinguishable' });
        return new ULRpcRequestMessage(procedureName, args, nonce, this.rpcId);
    };
}
